/**
 * Payment Service
 * API calls for payment management
 */

import api from './api.js';
import type { ApiResponse, PaginatedResponse } from '@/types/api.js';
import type {
  Payment,
  PaymentWithAllocations,
  PaymentCreateInput,
  PaymentListParams,
  PaymentStatistics,
  PaymentMethod,
} from '@/types/payment.js';

export const paymentsService = {
  /**
   * Get paginated list of payments
   */
  async list(params: PaymentListParams = {}): Promise<PaginatedResponse<Payment>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Payment>>>('/payments', {
      params,
    });
    return response.data.data;
  },

  /**
   * Get payment by ID
   */
  async getById(id: string): Promise<PaymentWithAllocations> {
    const response = await api.get<ApiResponse<PaymentWithAllocations>>(`/payments/${id}`);
    return response.data.data;
  },

  /**
   * Create new payment
   */
  async create(data: PaymentCreateInput): Promise<PaymentWithAllocations> {
    const response = await api.post<ApiResponse<PaymentWithAllocations>>('/payments', data);
    return response.data.data;
  },

  /**
   * Update payment
   */
  async update(
    id: string,
    data: Partial<PaymentCreateInput>
  ): Promise<PaymentWithAllocations> {
    const response = await api.put<ApiResponse<PaymentWithAllocations>>(
      `/payments/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete payment
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/payments/${id}`);
  },

  /**
   * Refund payment
   */
  async refund(
    id: string,
    data: { amount: number; reason?: string }
  ): Promise<Payment> {
    const response = await api.post<ApiResponse<Payment>>(`/payments/${id}/refund`, data);
    return response.data.data;
  },

  /**
   * Void payment
   */
  async void(id: string): Promise<Payment> {
    const response = await api.post<ApiResponse<Payment>>(`/payments/${id}/void`);
    return response.data.data;
  },

  /**
   * Get payment statistics
   */
  async getStatistics(): Promise<PaymentStatistics> {
    const response = await api.get<ApiResponse<PaymentStatistics>>('/payments/statistics');
    return response.data.data;
  },

  // Payment Methods

  /**
   * Get payment methods
   */
  async getMethods(): Promise<PaymentMethod[]> {
    const response = await api.get<ApiResponse<PaymentMethod[]>>('/payments/methods');
    return response.data.data;
  },

  /**
   * Create payment method
   */
  async createMethod(data: { name: string; type: string }): Promise<PaymentMethod> {
    const response = await api.post<ApiResponse<PaymentMethod>>('/payments/methods', data);
    return response.data.data;
  },

  /**
   * Update payment method
   */
  async updateMethod(
    id: string,
    data: Partial<{ name: string; type: string; isActive: boolean }>
  ): Promise<PaymentMethod> {
    const response = await api.put<ApiResponse<PaymentMethod>>(
      `/payments/methods/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete payment method
   */
  async deleteMethod(id: string): Promise<void> {
    await api.delete(`/payments/methods/${id}`);
  },
};

export default paymentsService;
